import { Button, ButtonProps } from '@mantine/core'
import { useMemo } from 'react'
import { omit } from 'lodash'
import { useMediaQuery } from '@mantine/hooks'

const SolidButton = (props: ButtonProps & React.ComponentPropsWithoutRef<'button'>) => {
  const variant = props.variant ?? 'primary' // primary, assistive
  const isMobile = useMediaQuery('(max-width: 767px)')
  const size = props.size ?? (isMobile ? 'md' : 'lg')
  const filteredProps = omit(props, ['variant', 'type', 'size'])

  const borderRadius = useMemo(() => {
    switch (size) {
      case 'sm':
        return 8
      case 'md':
        return 10
      case 'lg':
      default:
        return 12
    }
  }, [size])

  const rootStyles = useMemo(() => {
    switch (variant) {
      case 'assistive': {
        return props.disabled
          ? {
              backgroundColor: 'var(--Interaction-Disable)',
              color: 'var(--Label-Assistive)',
              borderRadius,
            }
          : {
              '--button-bg': 'var(--Fill-Normal)',
              '--button-hover': 'var(--Fill-Strong)',
              color: 'var(--Label-Neutral)',
              borderRadius,
            }
      }
      case 'primary':
      default: {
        return props.disabled
          ? {
              backgroundColor: 'var(--Interaction-Disable)',
              color: 'var(--Label-Assistive)',
              borderRadius,
            }
          : {
              '--button-bg': 'var(--Primary-Normal)',
              '--button-hover': 'var(--Primary-Strong)',
              borderRadius,
            }
      }
    }
  }, [size, props.disabled, variant])

  const fontSize = useMemo(() => {
    switch (size) {
      case 'sm':
        return '13px'
      case 'md':
        return '15px'
      case 'lg':
      default:
        return '16px'
    }
  }, [size])

  return (
    <Button
      type={props.type || 'button'}
      size={size}
      styles={{
        root: {
          ...rootStyles,
          fontSize,
          '--button-height-sm': '32px',
          '--button-height-md': '40px',
          '--button-height-lg': '48px',
          '--button-padding-x-sm': '14px',
          '--button-padding-x-md': '20px',
          '--button-padding-x-lg': '28px',
        },
      }}
      {...filteredProps}
    />
  )
}

export default SolidButton
